import { Component, EventEmitter, Input, OnInit, OnDestroy, OnChanges, Output, SimpleChanges,
  ChangeDetectorRef, ChangeDetectionStrategy, ViewChild, ElementRef, NgZone,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import * as _ from 'lodash';



import { Observable } from 'rxjs';

import { Option } from 'shared/form/select/option.interface';
import {fromEvent} from 'rxjs/internal/observable/fromEvent';
import {debounceTime, distinctUntilChanged, takeWhile} from 'rxjs/operators';


const selectAllOption = {
  option: 'select-all',
  title: 'Select all',
};

@Component({
  moduleId: module.id,
  selector: 'select-options',
  templateUrl: 'select-options.component.html',
  styleUrls: ['select-options.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectOptionsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() title: string = '';
  @Input() optionsList: Array<Option> = [];
  @Input() maxSize: number = 200;
  @Input() ghostValue: string;
  @Input() allowSelectAll: boolean = false;
  @Input() disableAll: boolean = false;

  @Output() optionSelected: EventEmitter<any> = new EventEmitter();
  @Output() allOptionsSelected: EventEmitter<any> = new EventEmitter();

  @ViewChild('optionsScroll', {static: true})
  optionsScroll: ElementRef | void;

  searchTerm = new UntypedFormControl();
  filteredOptionsList: Array<any> = [];
  private isActive: boolean = true;
  private optionsChunks: Array<any> = [];
  private activeChunk: number = 0;

  /*******************************************
   * COMPONENT GETTERS
   *******************************************/

  get selected(): Array<any> {
    return _.filter(this.optionsList, ['selected', true]);
  }

  get scrollHeight(): number {
    return _.get(this.optionsScroll, 'nativeElement.scrollHeight', 0);
  }

  get offsetHeight(): number {
    return _.get(this.optionsScroll, 'nativeElement.offsetHeight', 0);
  }

  constructor(
    private ref: ChangeDetectorRef,
    private ngZone: NgZone,
  ) {}

  /*******************************************
   * COMPONENT HOOKS
   *******************************************/

  ngOnChanges(changes: SimpleChanges) {
    if (changes.optionsList && changes.optionsList.currentValue) {
      this.splitIntoChunks(changes.optionsList.currentValue);
      this.updateFilteredOptions(this.optionsChunks[0]);
      this.searchTerm.reset();
      this.ref.markForCheck();
    }
  }

  ngOnInit() {
    this.searchTerm.valueChanges
    .pipe(
        distinctUntilChanged(),
        debounceTime(300),
        takeWhile(() => this.isActive)
    )
    .subscribe((value: string) => {
      if (!value) {
        this.splitIntoChunks(this.optionsList);
      } else {
        const filteredOptions = _.filter(this.optionsList, (option: any) => {
            return _.toLower(option.title).indexOf(_.toLower(value)) !== -1;
          });
        this.splitIntoChunks(filteredOptions);
      }
      this.updateFilteredOptions(this.optionsChunks[0]);
    });
    this.subscribeOptionsScroll();
  }

  ngOnDestroy() {
    this.isActive = false;
    this.filteredOptionsList = [];
    this.optionsChunks = [];
  }

  /*******************************************
   * COMPONENT HANDLERS
   *******************************************/

  onOptionSelect(option: any) {
    option.selected = !option.selected;
    // TODO: make this universal - apply to the advanced search
    if (option.option === 'select-all') {
      // set all options selected
      _.forEach(this.optionsList, (opt: any) => {
        opt.selected = option.selected;
      });

      // set all chunks selected
      _.forEach(this.optionsChunks, (chunk: any) => {
        _.forEach(chunk, item => {
          item.selected = option.selected;
        });
      });

      this.allOptionsSelected.emit(_.filter(this.optionsList, opt => {
        return opt.selected && opt.option !== selectAllOption.option;
      }));
    } else {
      if (this.allowSelectAll) {
        // @ts-ignore
          const opt = _.find(this.filteredOptionsList, selectAllOption);
        if (opt) { opt.selected = false; }
      }

      // set option selected in options list
      const optionListItem = _.find(this.optionsList, {option: option.option});
      if (optionListItem) {
          // @ts-ignore
          optionListItem.selected = option.selected;
      }

      _.forEach(this.optionsChunks, (chunk: any) => {
        const chunkItem = _.find(this.optionsList, {option: option.option});
        if (chunkItem) {
            // @ts-ignore
            chunkItem.selected = option.selected;
        }
      });

      this.optionSelected.emit(option);
    }
  }

  onClearClick() {
    this.searchTerm.reset();
    _.each(this.optionsList, (option: any) => {
      if (option.selected) { option.selected = false; }
    });
    if (this.allowSelectAll && this.filteredOptionsList) {
      _.set(this.filteredOptionsList[0], 'selected', false);
    }
    this.ref.markForCheck();
  }

  /*******************************************
   * COMPONENT PRIVATE METHODS
   *******************************************/

  private splitIntoChunks(data: Array<any>): void {
    this.optionsChunks = _.chunk(data, this.maxSize);
    this.activeChunk = 0;
  }

  private subscribeOptionsScroll(): void {
    this.ngZone.runOutsideAngular(() => {
      if (!this.optionsScroll) {
        return;
      }
      fromEvent(this.optionsScroll.nativeElement, 'scroll')
      .pipe(takeWhile(() => this.isActive))
      .subscribe((event: any) => {
        // after scrolling to the end try to load next chunk
        if (event.currentTarget.scrollTop + this.offsetHeight >= this.scrollHeight) {
          if (_.size(this.optionsList) <= this.maxSize) { return; }
          const nextChunkNumber = this.activeChunk + 1;
          if (!this.optionsChunks[nextChunkNumber]) { return; }
          this.filteredOptionsList = _.concat(this.filteredOptionsList, this.optionsChunks[nextChunkNumber]);
          this.activeChunk += 1;
          this.ref.markForCheck();
        }
      });
    });
  }

  private updateFilteredOptions(options: Array<any>) {
    this.filteredOptionsList = options;
    if (this.filteredOptionsList && this.allowSelectAll) {
      this.filteredOptionsList.unshift({...selectAllOption});
    }
    this.ref.markForCheck();
  }
}
